import { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { TableCell, TableRow, TableBody, Typography } from "@mui/material";
import {
  Select,
  MenuItem,
  Pagination,
  Paper,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import Controls from "../../../components/controls/Controls";
import useTable from "../../../components/customHelpers/usePaginationTable";
import Loading from "../../../components/customHelpers/Loading";
import Norecords from "../../../components/customHelpers/Norecords";
import Popup from "../../../components/customHelpers/Popup";
import UpdateContainer from "../../../components/forms/updateContainer";
import Product from "../../../actions/product";
import { FiEdit } from "react-icons/fi";
import { pageSizes } from "../../../utils/utils";
import { Search } from "@mui/icons-material";

const productHeadCells = [
  { id: "name", label: "Name" },
  { id: "quantity", label: "Quantity" },
  { id: "price", label: "Price" },
  { id: "productName", label: "Product Name" },
  { id: "sku", label: "Sku" },
  { id: "store", label: "Store" },
  { id: "createdAt", label: "Date" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const ProductContainers = ({ NotifyMessage }) => {
  const { viewAllProductsByContainerWithPages } = Product();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(pageSizes[1]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [actualTotal, setActualTotal] = useState(0);
  const [tot, setTot] = useState(1);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [edite, setEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [productLists, setProductLists] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { viewAllProductsLists } = Product();
  const [filterFn, setFilterFn] = useState({
    fn: (items) => items,
  });

  useEffect(() => {
    getProductsByContainer();
  }, [page, limit, searchQuery, selectedProduct]);

  useEffect(() => {
    getProductData();
  }, []);

  const getProductsByContainer = () => {
    setLoading(true);
    viewAllProductsByContainerWithPages(
      page,
      limit,
      searchQuery,
      null,
      selectedProduct?.product.product.sku
    ).then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.product) {
        const transformedData = data.product.map((item) => ({
          ...item,
          productName: item.productList?.product.name || "",
          sku: item.productList?.product.sku || "",
          store: item.productList?.store.locationName || "",
          price: item.productList?.price || "",
        }));
        setContainers(transformedData);
        setTotalPages(Math.ceil(data.total / limit));
        setActualTotal(data.pagination.total);
        setTot(data.pagination.totalPages);
      }
      setLoading(false);
    });
  };

  const getProductData = () => {
    viewAllProductsLists().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.product) {
        const uniqueProducts = data.product.filter(
          (product, index, self) =>
            index ===
            self.findIndex((p) => p.product.sku === product.product.sku)
        );
        setProductLists(uniqueProducts);
      }
    });
  };

  const handleSearchChange = (event, value) => {
    setPage(1);
    setSelectedProduct(value);
  };

  const options = productLists.map((product) => ({
    label: `${product.product.sku} - ${product.product.name}`,
    product,
  }));

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (e) => {
    setPage(1);
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const { TblContainer, TblHead, recordsAfterSortingAndFiltering } = useTable(
    containers,
    productHeadCells,
    filterFn
  );

  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(startIndex + limit - 1, actualTotal);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <div
          className="search-header-container"
          style={{ marginBottom: 0, flex: 1, maxWidth: 260, minWidth: 260 }}
        >
          <div className="search-header-flex">
            <div className="search-container">
              <Search className="search_icon" />
              <input
                type="text"
                onChange={handleSearch}
                placeholder="Search Containers"
                className="search-input"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
        <Box display="flex" alignItems="flex-end" gap={2}>
          <Autocomplete
            options={searchTerm ? filteredOptions : []}
            getOptionLabel={(option) => option.label}
            onChange={handleSearchChange}
            onInputChange={(event, newInputValue, reason) => {
              setInputValue(newInputValue);
              if (reason === "clear") {
                setSearchTerm("");
              }
            }}
            value={selectedProduct}
            inputValue={inputValue}
            sx={{
              maxWidth: 260,
              minWidth: 260,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SKU or Product Name"
                variant="outlined"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          />
        </Box>
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <Paper style={{ overflowX: "auto" }}>
          <TblContainer>
            <TblHead />
            <TableBody>
              {recordsAfterSortingAndFiltering().length > 0 ? (
                recordsAfterSortingAndFiltering().map((item) => (
                  <TableRow key={item.Id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.price}</TableCell>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell>{item.store}</TableCell>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Controls.ActionButton
                        color="primary"
                        onClick={() => {
                          setOpenPopup(true);
                          setRecordForEdit(item);
                          setEdit(true);
                        }}
                      >
                        <FiEdit />
                      </Controls.ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <Norecords col={8} />
              )}
            </TableBody>
          </TblContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              p: 2,
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Rows per page:
              </Typography>
              <Select
                value={limit}
                onChange={handlePageSizeChange}
                size="small"
                sx={{ minWidth: 120 }}
              >
                {pageSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {actualTotal && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {startIndex}-{endIndex} of {actualTotal}
              </Typography>
            )}
            {containers.length > 0 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                hideNextButton={page === tot}
                hidePrevButton={page === 1}
                shape="rounded"
                siblingCount={1}
              />
            )}
          </Box>
        </Paper>
      )}
      <Popup
        title="Product Container Form"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <UpdateContainer
          openPopup={openPopup}
          NotifyMessage={NotifyMessage}
          setOpenPopup={setOpenPopup}
          recordForEdit={recordForEdit}
          getProductsByContainer={getProductsByContainer}
          isProductContainer={true}
        />
      </Popup>
    </>
  );
};

export default ProductContainers;
